import type { Delivery, Schedule, Settings } from '../storage/schemas';
import type { NotificationAdapter, ReminderPayload } from '../notifications/adapter';
import { nextOccurrence, occurrencesBetween } from './recurrence';
import { instantToWall, parseTime, wallToInstant } from './tz';

/** Reminders found later than this after their time are recorded as "missed" instead of popping up. */
export const LATE_GRACE_MS = 15 * 60_000;
/** Never look back further than this when the app was closed for a long time. */
const MAX_CATCH_UP_MS = 7 * 864e5;

export interface DueReminder {
  key: string;
  schedule: Schedule;
  occurrence: number;
  fireAt: number;
}

export function occurrenceKey(scheduleId: string, occurrence: number): string {
  return `${scheduleId}@${new Date(occurrence).toISOString()}`;
}

/** Shift `t` out of quiet hours (local to `tz`); returns `t` unchanged when outside them. */
export function applyQuietHours(t: number, quiet: Settings['quietHours'], tz: string): number {
  if (!quiet.enabled || quiet.start === quiet.end) return t;
  const w = instantToWall(t, tz);
  const mins = w.hh * 60 + w.mm;
  const s = parseTime(quiet.start);
  const e = parseTime(quiet.end);
  const start = s.hh * 60 + s.mm;
  const end = e.hh * 60 + e.mm;
  const inside = start < end ? mins >= start && mins < end : mins >= start || mins < end;
  if (!inside) return t;
  // End of the quiet window: same local day if it ends later today, otherwise tomorrow.
  const endsTomorrow = start > end && mins >= start;
  const base = new Date(Date.UTC(w.y, w.m - 1, w.d + (endsTomorrow ? 1 : 0)));
  return wallToInstant({ y: base.getUTCFullYear(), m: base.getUTCMonth() + 1, d: base.getUTCDate(), hh: e.hh, mm: e.mm }, tz);
}

/** Reminder fire times in (from, to] for every enabled, notifying schedule (plus snoozes). */
export function dueBetween(schedules: Schedule[], from: number, to: number, quiet: Settings['quietHours']): DueReminder[] {
  const out: DueReminder[] = [];
  for (const s of schedules) {
    if (!s.enabled || !s.notify) continue;
    const lead = s.leadMinutes * 60_000;
    // Occurrences whose (lead-shifted, quiet-adjusted) fire time could fall in the window.
    const occ = occurrencesBetween(s.rule, from + lead - 864e5, to + lead + 1);
    for (const o of occ) {
      const fireAt = applyQuietHours(o - lead, quiet, s.rule.timeZone);
      if (fireAt > from && fireAt <= to) out.push({ key: occurrenceKey(s.id, o), schedule: s, occurrence: o, fireAt });
    }
    if (s.snoozedUntil && s.snoozedUntil > from && s.snoozedUntil <= to) {
      out.push({ key: `${s.id}@snooze@${new Date(s.snoozedUntil).toISOString()}`, schedule: s, occurrence: s.snoozedUntil, fireAt: s.snoozedUntil });
    }
  }
  return out.sort((a, b) => a.fireAt - b.fireAt);
}

export function nextFireTime(schedules: Schedule[], after: number, quiet: Settings['quietHours']): number | null {
  let best: number | null = null;
  for (const s of schedules) {
    if (!s.enabled) continue;
    const lead = s.notify ? s.leadMinutes * 60_000 : 0;
    // Look a little ahead so a lead time does not skip the occurrence that is about to happen.
    let o = nextOccurrence(s.rule, after + lead - 1);
    while (o !== null && o - lead <= after) o = nextOccurrence(s.rule, o);
    if (o !== null) {
      const t = s.notify ? applyQuietHours(o - lead, quiet, s.rule.timeZone) : o;
      if (best === null || t < best) best = t;
    }
    if (s.snoozedUntil && s.snoozedUntil > after && (best === null || s.snoozedUntil < best)) best = s.snoozedUntil;
  }
  return best;
}

export interface SchedulerDeps {
  now: () => number;
  getSchedules: () => Schedule[];
  getSettings: () => Settings;
  noteTitle: (noteId: string) => string;
  getLastCheck: () => number;
  setLastCheck: (t: number) => void;
  claim: (d: Delivery) => Promise<boolean>;
  adapter: NotificationAdapter & { channelFor?: () => 'system' | 'in-app' };
  onMissed?: (r: DueReminder) => void;
  /** Called on every tick so the UI can re-resolve the scheduled active note. */
  onTick?: () => void;
}

/**
 * In-app scheduler. One timer is armed for the next fire time and re-armed after every check.
 * It only runs while the page is alive; see docs/platform-capabilities.md.
 */
export class Scheduler {
  private timer: ReturnType<typeof setTimeout> | null = null;
  private running = false;
  private checking: Promise<void> | null = null;

  constructor(private readonly deps: SchedulerDeps) {}

  start(): void {
    this.running = true;
    void this.check();
  }

  stop(): void {
    this.running = false;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }

  /** Deliver everything due since the last check, exactly once each. Safe to call often. */
  check(): Promise<void> {
    if (this.checking) return this.checking;
    this.checking = this.runCheck().finally(() => {
      this.checking = null;
      this.arm();
    });
    return this.checking;
  }

  private async runCheck(): Promise<void> {
    const now = this.deps.now();
    const last = Math.max(this.deps.getLastCheck(), now - MAX_CATCH_UP_MS);
    const settings = this.deps.getSettings();
    const due = dueBetween(this.deps.getSchedules(), last, now, settings.quietHours);
    for (const r of due) {
      const late = now - r.fireAt > LATE_GRACE_MS;
      const channel = late ? 'none' : (this.deps.adapter.channelFor?.() ?? this.deps.adapter.id);
      const claimed = await this.deps.claim({
        key: r.key,
        scheduleId: r.schedule.id,
        noteId: r.schedule.noteId,
        occurrence: r.occurrence,
        firedAt: now,
        status: late ? 'missed' : 'delivered',
        channel,
      });
      if (!claimed) continue; // already delivered (another tab or an earlier run)
      if (late) {
        this.deps.onMissed?.(r);
        continue;
      }
      const payload: ReminderPayload = {
        key: r.key,
        scheduleId: r.schedule.id,
        noteId: r.schedule.noteId,
        title: r.schedule.title || this.deps.noteTitle(r.schedule.noteId) || 'MemoNest reminder',
        body: r.schedule.message || this.deps.noteTitle(r.schedule.noteId),
        occurrence: r.occurrence,
      };
      await this.deps.adapter.show(payload);
    }
    this.deps.setLastCheck(now);
    this.deps.onTick?.();
  }

  private arm(): void {
    if (!this.running) return;
    if (this.timer) clearTimeout(this.timer);
    const now = this.deps.now();
    const next = nextFireTime(this.deps.getSchedules(), now, this.deps.getSettings().quietHours);
    // Re-check at least every minute (clock changes, sleep/wake), and exactly at the next fire time.
    const delay = Math.max(250, Math.min(next === null ? 60_000 : next - now + 50, 60_000));
    this.timer = setTimeout(() => void this.check(), delay);
  }
}
